import React, { useEffect, useMemo, useState } from 'react';
import { EnergyLogo } from './logos/EnergyLogo';
import { CommonGridReport } from './custom-flows/report/CommonGridReport';
import useGenerateReport from '@/hooks/useGenerateReport';
import EnergyResultsModal from './modals/EnergyResultModal';
import { ProgressLoader } from './ProgressLoader';
import { VITE_NODE_ENV } from '@/utils/getEnv';

function formatToCET(dateString?: string): string {
  if (!dateString) {
    return 'Invalid date';
  }

  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }

  // Using Europe/Berlin timezone as CET/CEST example
  const options: Intl.DateTimeFormatOptions = {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  };
  const parts = new Intl.DateTimeFormat('en-GB', options).formatToParts(date);
  const partsObj = Object.fromEntries(parts.map(p => [p.type, p.value]));

  // Format as "YYYY-MM-DD, HH:mm:ss"
  return `${partsObj.year}-${partsObj.month}-${partsObj.day}, ${partsObj.hour}:${partsObj.minute}:${partsObj.second}`;
}

const clientCheckLogoColors = {
  'leonhard-weiss': {
    outerFill: '#0C111D',
    innerFill: '#ffffff',
  },
  gridty: {
    outerFill: '#CCFF66',
    innerFill: '#132626',
  },
};

const logo = VITE_NODE_ENV['VITE_THEME'] as keyof typeof clientCheckLogoColors;

export const EnergyReport = ({ uuid, upsertLoading = false }: { uuid: string; upsertLoading?: boolean }) => {
  const [generateReport, { data: report, loading, animationComplete }] = useGenerateReport();
  const [mailReport, setMailReport] = useState(false);

  useEffect(() => {
    generateReport(uuid);
  }, []);

  const data = useMemo(
    () => [
      {
        cards: [
          {
            label: 'Anlagengröße',
            value: report?.results?.pv_kwp
              ? report?.results?.pv_kwp?.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
              : '0',
            unit: 'kWp',
          },
          {
            label: 'Jahresertrag',
            value: report?.results?.pv_annual_yield
              ? Math.round(report?.results?.pv_annual_yield)?.toLocaleString('de-DE')
              : '0',
            unit: 'kWh',
          },
          {
            label: 'Eigenverbrauchsquote (EVQ)',
            value: report?.results?.pv_evq
              ? report?.results?.pv_evq?.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
              : '0',
            unit: '%',
          },
        ],
        highlight: {
          label: 'Mögliche CO₂ Einsparung',
          value: report?.results?.pv_co2_float
            ? report?.results?.pv_co2_float?.toLocaleString('de-DE', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })
            : '0',
          unit: 'tCO2e',
        },
        paybackPeriod: {
          label: 'Amortisationszeit',
          value: report?.results?.pv_transition_point
            ? report?.results?.pv_transition_point?.toLocaleString('de-DE', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })
            : '0',
          unit: 'Jahre',
        },
        investmentCost: report?.results?.pv_capex
          ? `${Math.round(report?.results?.pv_capex)?.toLocaleString('de-DE')} €`
          : '0 €',
        presentValue: report?.results?.pv_npv
          ? `${Math.round(report?.results?.pv_npv)?.toLocaleString('de-DE')} €`
          : '0 €',
      },
    ],
    [report?.results],
  );

  const batteryData = useMemo(
    () => [
      {
        cards: [
          {
            label: 'Mögliche Speichergröße',
            value: report?.results?.bat_system_size ?? '0',
            unit: 'kWh',
          },
          {
            label: 'Investitionskosten',
            value: report?.results?.bat_capex
              ? `${Math.round(report?.results?.bat_capex)?.toLocaleString('de-DE')} €`
              : '0 €',
            price: true,
          },
          {
            label: 'Barwert (bei 6% WACC)',
            value: report?.results?.bat_npv
              ? `${Math.round(report?.results?.bat_npv)?.toLocaleString('de-DE')} €`
              : '0 €',
            price: true,
          },
          // ...other fields
        ],
        highlight: {
          label: 'Mögliche CO₂ Einsparung',
          value: report?.results?.pv_co2_float
            ? report?.results?.bat_co2?.toLocaleString('de-DE', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })
            : '0',
          unit: 'tCO2e',
        },
        paybackPeriod: {
          label: 'Amortisationszeit',
          value: report?.results?.bat_transition_point
            ? report?.results?.bat_transition_point?.toLocaleString('de-DE', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })
            : '0',
          unit: 'Jahre',
        },
      },
    ],
    [report?.results],
  );

  const hpData = useMemo(
    () => [
      {
        cards: [
          {
            label: 'Wärmepumpentyp',
            value: 'Luft/Wasser', // This is hardcoded in the document
          },
          {
            label: 'Jahresarbeitszahl (JAZ)',
            value: report?.results?.hp_jaz_fo ? report?.results?.hp_jaz_fo?.toFixed(1) : '1',
          },
          {
            label: 'Investitionskosten (inkl. 30% Förderung)',
            value: report?.results?.hp_capex_fo
              ? `${Math.round(report?.results?.hp_capex_fo)?.toLocaleString('de-DE')} €`
              : '0 €',
          },
          {
            label: 'Nennleistung',
            value: report?.results?.hp_nominal_power_fo ? report?.results?.hp_nominal_power_fo?.toFixed(1) : '0',
            unit: 'kW',
          },
          {
            label: 'Bivalenzpunkt',
            value: report?.results?.hp_bivalence_point_fo ? report?.results?.hp_bivalence_point_fo?.toFixed(1) : '1',
            unit: '°C',
          },
          {
            label: 'Jährlicher Stromverbrauch',
            value: report?.results?.hp_electricity_consumption_fo
              ? Math.round(report?.results?.hp_electricity_consumption_fo)?.toLocaleString('de-DE')
              : '0',
            unit: 'kWh',
          },
        ],
        paybackPeriod: {
          label: 'Amortisationszeit',
          value: report?.results?.hp_transition_point_fo ? report?.results?.hp_transition_point_fo?.toFixed(1) : '0',
          unit: 'Jahre',
        },
        highlight: {
          label: 'Mögliche CO₂ Einsparung',
          value: report?.results?.hp_co2_emissions_saved_fo
            ? report?.results?.hp_co2_emissions_saved_fo?.toLocaleString('de-DE', {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })
            : '0',
          unit: 'tCO2e',
        },
      },
    ],
    [report?.results],
  );

  const charging = useMemo(
    () => [
      {
        cards: [
          {
            label: 'Ladesäulen',
            value:
              report?.results?.count_normal_ev_total || report?.results?.count_normal_public_ev
                ? `${report?.results?.count_normal_ev_total} (davon ${report?.results?.count_normal_public_ev} öffentl.)`
                : '0',
          },
          {
            label: 'Schnellladesäulen',
            value:
              report?.results?.count_fast_ev_total || report?.results?.count_fast_public_ev
                ? `${report?.results?.count_fast_ev_total} (davon ${report?.results?.count_fast_public_ev} öffentl.)`
                : '0',
          },
          {
            label: 'Investitionskosten',
            value: report?.results?.capex_total_ev ? `${report?.results?.capex_total_ev} €` : '0 €',
          },
          {
            label: 'Mögliche Einsparungen durch Mehrnutzung PV',
            value: report?.results?.av_annual_savings_evq_incr_ev
              ? `${Math.round(report?.results?.av_annual_savings_evq_incr_ev)} €/Jahr`
              : '0 €',
          },
          {
            label: 'Mögliche THG-Einsparung',
            value: report?.results?.ev_thg_annual_total
              ? `${Math.round(report?.results?.ev_thg_annual_total)} €/Jahr`
              : '0 t',
          },
          {
            label: 'Mögliches Einkommen durch öfftl. Ladesäulen',
            value: report?.results?.ev_av_annual_net_income
              ? `${Math.round(report?.results?.ev_av_annual_net_income)} €/Jahr`
              : '0 €',
          },
        ],
      },
    ],
    [report?.results],
  );

  return (
    <>
      {upsertLoading || loading ? (
        <div className="flex flex-col gap-[20px] justify-center items-center min-h-[80vh] text-center">
          <ProgressLoader isComplete={animationComplete} />
          <p className="text-[28px] font-semibold text-primary">
            Wir analysieren Ihre Eingaben und erstellen Ihre Ergebnisse. Dauer ca. 15-30 Sekunden.
          </p>
        </div>
      ) : (
        <div>
          <div className="flex flex-col md:flex-row justify-between md:items-center md:mt-0 mt-[180px] mb-[20px]">
            <div className="flex gap-[20px] items-center">
              <EnergyLogo
                innerFill={clientCheckLogoColors[logo]?.innerFill}
                outerFill={clientCheckLogoColors[logo]?.outerFill}
              />
              <div className="flex flex-col gap-[10px]">
                <h3 className="text-[28px] text-primary font-semibold">Ihre Ergebnisse</h3>
                <p className="text-[18px] text-primary font-normal">
                  Berechnet am : {formatToCET(report?.scenario?.created_at)}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setMailReport(true)}
              className="font-bold bg-warning text-[#000000] px-[24px] py-[12px] flex justify-center gap-[10px] rounded-[6px] mt-[12px] md:mt-0"
             > Erfahren Sie mehr!
            </button>
          </div>
          <div className="flex flex-col gap-[16px]">
            <CommonGridReport
              title="PV-Anlage"
              data={data}
              grid={2}
              chart
              report={report?.results?.pv_plot_amortisation}
              dataNotAvailable={report?.results?.pv_kwp == 0}
              notes={`Spez. CAPEX ${report?.scenario?.specific_capex_pv || 0} €/kWp, OPEX ${report?.scenario?.specific_opex_pv || 0} % des CAPEX/a, ${report?.scenario?.yearly_electricity_cost_increase || 0}% jährliche Stromkostensteigerung, Emissionsfaktor 0.4 kgCO2e/kWh. Unverbindliche Vorabschätzung auf Basis Ihrer Angaben.`}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px]">
              <CommonGridReport
                title="Batteriespeicher"
                data={batteryData}
                grid={1}
                chart
                report={report?.results?.bat_plot_amortisation}
                dataNotAvailable={report?.results?.bat_system_size == 0}
                notes={`Spez. CAPEX ${report?.scenario?.specific_capex_bat || 0} €/kWh, OPEX ${report?.scenario?.specific_opex_bat || 0} % des CAPEX/a, ${report?.scenario?.yearly_electricity_cost_increase || 0}% jährliche Stromkostensteigerung, C-Rate: ${report?.scenario?.c_rate_bat || 0}, Zelltyp: Li-Ionen. Die Nutzung mit dynamischem Stromtarif bietet weitere Einsparpotentiale und Möglichkeiten. Unverbindliche Vorabschätzung auf Basis Ihrer Angaben.`}
              />
              <CommonGridReport
                title="Wärmepumpe"
                data={hpData}
                grid={1}
                chart
                report={report?.results?.hp_plot_amortisation}
                dataNotAvailable={report?.results?.hp_capex_fo === null || report?.results?.hp_capex_fo === undefined || report?.results?.hp_capex_fo === 0}
                notes={`Spez. CAPEX ${report?.scenario?.specific_capex_hp || 0} €/kW incl. 30% Förderung, OPEX ${report?.scenario?.specific_opex_hp || 0} % des CAPEX/a, ${report?.scenario?.yearly_electricity_cost_increase || 0}% jährliche Stromkostensteigerung, Emissionsfaktor Strom 0.4 kgCO2e/kWh, Emissionsfaktoren Gas/Öl 0.2/0.26 kgCO2e/kWh. Unverbindliche Vorabschätzung auf Basis Ihrer Angaben.`}
              />
              <CommonGridReport
                title="Ladesäulen"
                data={charging}
                grid={1}
                dataNotAvailable={
                  report?.results?.count_normal_ev_total == 0 &&
                  report?.results?.count_normal_public_ev == 0 &&
                  report?.results?.count_fast_ev_total == 0 &&
                  report?.results?.count_fast_public_ev == 0
                }
                notes={`CAPEX reguläre Ladesäule: ${report?.scenario?.cost_ev_ac || 0} €; Schnellladesäule: ${report?.scenario?.cost_ev_dc || 0}, ⌀ Ladedauer reguläre Ladesäule: ${report?.scenario?.hours_per_day_ac || 0} h, ⌀ Ladedauer Schnellladesäule: ${report?.scenario?.hours_per_day_dc || 0} h, ⌀ Verkaufspreis öfftl. Schnellladesäule: ${report?.scenario?.base_price_normal || 0} Cent/kWh, ⌀ Verkaufspreis Schnellladesäule: ${report?.scenario?.base_price_fast || 0} Cent/kWh. Unverbindliche Vorabschätzung auf Basis Ihrer Angaben.`}
              />
            </div>
          </div>

          <EnergyResultsModal
            isVisible={mailReport}
            onClose={() => setMailReport(false)}
            onSubmit={() => setMailReport(false)}
            uuid={uuid}
          />
        </div>
      )}
    </>
  );
};
